/* eslint-disable no-plusplus,no-underscore-dangle,no-nested-ternary */
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { naturalSorting } from '@/utils/experience-utils';
import { VSpace } from '@/components/spacing';
import { Tooltip } from '@/components/tooltip';
import { getChanceToWin, getUplift } from '@/features/details/summary/util';
import {
  Header,
  SummaryHeader,
  useMetricName,
} from '@/features/details/summary/summary-header';
import {
  SummaryBody,
  SummaryValue,
  VersionName,
} from '@/features/details/summary/summary-value';
import { Card } from '@/components/card';
import { SummaryContext } from '@/features/details/summary/context';
import { ExpVersion, SummaryResponse } from '@/webapi/use-analytics-api';
import {
  Experience,
  ExperienceStatus,
  ExperienceVariant,
} from '@/webapi/use-experience-api';
import { allMetricKinds, MetricKind } from '@/webapi/models';
import { maybe } from '@/features/details/utils';
import { CONTROL } from '@/features/details/shared';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';
import { DatePickerContext } from '@/features/details/date-pick/context';
import {
  allowCustomExpEvents,
  allowedCustomMetrics,
  allowProfit,
  allowReturns,
} from '@/features/details/charts/more-metrics';

import {
  CIHedaer,
  ConfidenceIntervals,
} from '@/features/details/summary/confidence-interval';
import { MoreMetricsList } from '@/features/details/summary/see-more-reports-list';

function OtwTooltip({ text }: { text: string }) {
  return (
    <Tooltip
      zIndex={0}
      title="Odds to Win"
      text={
        <span>
          The probability Version A is Better than Version B. <br />
          Based on the primary goal
          <strong>( {text} )</strong>.
        </span>
      }
    />
  );
}

export function SummaryTable({
  status,
  experience,
  setExperience,
  ciEnabled,
}: {
  status: ExperienceStatus;
  experience: Experience;
  setExperience: (e: Experience) => void;
  ciEnabled?: boolean;
}) {
  const {
    moreMetrics,
    metricsPerRow,
    showSummary,
    variants,
    hasOneVariant,
    data,
    primaryMetrics,
    largeHeaders,
    hasMoreMetrics,
    primaryGoal,
    hasMetric,
  } = useContext(SummaryContext);
  const allMetrics = allMetricKinds(experience)
    .filter(allowProfit(experience))
    .filter(allowedCustomMetrics(hasMetric))
    .filter(allowReturns(experience))
    .filter(allowCustomExpEvents(experience));
  const addMoreMetrics = useMemo(
    () =>
      allMetrics?.filter(
        (value) =>
          !primaryMetrics?.includes(value) && !moreMetrics?.includes(value),
      ),
    [experience, primaryMetrics, moreMetrics],
  );

  const { version } = useContext(DatePickerContext);
  const _variants = filterActiveVariants(variants, version, status);
  const { title: goalTitle } = useMetricName(primaryGoal, false, ``, ``);
  const confidenceIntervalOffset = ciEnabled && !hasOneVariant ? 1 : 0;

  if (maybe(() => _variants.length, 0) === 0 || !showSummary) {
    return null;
  }
  return (
    <>
      <DetailsCard hasOneVariant={hasOneVariant}>
        <SummaryRow
          key="iddqd"
          columns={metricsPerRow + confidenceIntervalOffset}
        >
          {!hasOneVariant && (
            <SummaryHeader isLarge={largeHeaders}>Variation</SummaryHeader>
          )}
          {primaryMetrics.map((h) => (
            <Header value={h} key={h} isLarge={largeHeaders} />
          ))}
          {!hasOneVariant && (
            <>
              <SummaryHeader>
                {ciEnabled && !hasOneVariant ? `OTW` : `Odds to Win`}
                <OtwTooltip text={goalTitle} />
              </SummaryHeader>
              {ciEnabled && <CIHedaer />}
            </>
          )}
        </SummaryRow>
        {_variants?.sort(naturalSorting).map((v) => (
          <SummaryRow
            mt={ciEnabled ? `15px` : 0}
            key={v.id}
            columns={metricsPerRow + confidenceIntervalOffset}
          >
            {!hasOneVariant && <VersionName v={v} />}
            {primaryMetrics.map((metric) => (
              <SummaryValue key={metric} metric={metric} variant={v} />
            ))}
            {!hasOneVariant && (
              <SummaryBody>
                <span>
                  {getFormattedChanceToWin({
                    data,
                    v,
                    primaryGoal,
                    variants: _variants,
                  })}
                </span>
              </SummaryBody>
            )}
            {ciEnabled && !hasOneVariant && (
              <ConfidenceIntervals
                variant={v}
                uplift={getUplift(data, primaryGoal, variants, v.id)}
              />
            )}
          </SummaryRow>
        ))}
      </DetailsCard>
      {hasMoreMetrics && (
        <>
          <VSpace value={2} />
          <MoreMetricsSection
            key="mm-0-6"
            _variants={_variants}
            rowsFilter={(_, idx) => idx < 7}
            experience={experience}
            setExperience={setExperience}
            addMoreMetrics={addMoreMetrics}
            shouldShow={
              moreMetrics?.length <= (hasOneVariant ? 7 : 6) &&
              addMoreMetrics?.length > 0
            }
          />
        </>
      )}

      {moreMetrics?.length > (hasOneVariant ? 7 : 6) && (
        <>
          <VSpace value={2} />
          <MoreMetricsSection
            key="mm-7-13"
            _variants={_variants}
            rowsFilter={(_, idx) => idx < 14 && idx > 6}
            experience={experience}
            setExperience={setExperience}
            addMoreMetrics={addMoreMetrics}
            shouldShow={moreMetrics?.length < 14 && addMoreMetrics?.length > 0}
            size={(hasOneVariant ? 8 : 7) - (moreMetrics.length - 7)}
          />
        </>
      )}
      {moreMetrics?.length >= 14 && (
        <>
          <VSpace value={2} />
          <MoreMetricsSection
            key="mm-14+"
            _variants={_variants}
            rowsFilter={(_, idx) => idx > 13}
            experience={experience}
            setExperience={setExperience}
            addMoreMetrics={addMoreMetrics}
            shouldShow={moreMetrics?.length < 21 && addMoreMetrics?.length > 0}
            size={(hasOneVariant ? 8 : 7) - (moreMetrics.length - 14)}
          />
        </>
      )}
    </>
  );
}

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p: any) => p.columns}, 1fr);
  grid-column-gap: 2rem;
  text-align: start;
  margin-top: ${(p) => p.mt};
`;

const DetailsCard = styled(Card)`
  && {
    cursor: default;
    display: grid;
    position: relative;
    grid-row-gap: 2.5rem;
    min-height: 150px;
    padding: ${(p: { hasOneVariant: boolean }) =>
      p?.hasOneVariant ? `3.5rem 5rem 4rem 5rem` : `3.5rem`};
    border-radius: 2rem;
    user-select: unset;
    && * {
      user-select: unset;
    }
  }
`;

function MoreMetricsSection({
  _variants,
  experience,
  setExperience,
  addMoreMetrics,
  shouldShow,
  size,
  rowsFilter,
}: {
  _variants: Array<ExperienceVariant>;
  experience: Experience;
  setExperience: (e: Experience) => void;
  addMoreMetrics: MetricKind[];
  shouldShow: boolean;
  size?: number;
  rowsFilter?: (_, idx) => boolean;
}) {
  const { moreMetrics, metricsPerRow, hasOneVariant, largeHeaders } =
    useContext(SummaryContext);
  const metricKinds = moreMetrics.filter(rowsFilter);

  return (
    <DetailsCard>
      <SummaryRow columns={metricsPerRow}>
        {!hasOneVariant && <SummaryHeader>Variation</SummaryHeader>}
        {metricKinds.map((metric) => (
          <Header key={metric} value={metric} isLarge={largeHeaders} />
        ))}
      </SummaryRow>
      {_variants?.sort(naturalSorting).map((v) => (
        <SummaryRow key={v.id} columns={metricsPerRow}>
          {!hasOneVariant && <VersionName v={v} />}
          {metricKinds.map((metric) => (
            <SummaryValue key={metric} metric={metric} variant={v} />
          ))}
        </SummaryRow>
      ))}
      <MoreMetricsList
        experience={experience}
        setExperience={setExperience}
        hasOneVariant={hasOneVariant}
        moreMetrics={moreMetrics}
        addMoreMetrics={addMoreMetrics}
        shouldShow={shouldShow}
        size={size}
      />
    </DetailsCard>
  );
}

function isMultiVariantNoControl(
  hasControl: boolean,
  variants: Array<ExperienceVariant>,
) {
  return !hasControl && variants?.length >= 2;
}

export function getFormattedChanceToWin({
  data,
  v,
  primaryGoal,
  variants,
}: {
  data: SummaryResponse;
  v: ExperienceVariant;
  primaryGoal: MetricKind;
  variants: Array<ExperienceVariant>;
}) {
  let hasControl = dataHasControl(variants, data);
  if (isMultiVariantNoControl(hasControl, variants)) {
    data = clone(data);
    if (maybe(() => v.id === data.controlVariationId)) {
      v = clone(v);
      v.name = CONTROL;
    }
    hasControl = true;
  }
  let chanceToWin: number | string = getChanceToWin(data, v, primaryGoal);
  const isMultiVariantControl = variants?.length > 2 && v.name === CONTROL;

  if (!hasControl) {
    return `---`;
  }

  if (chanceToWin <= 1) {
    chanceToWin = `<1`;
  }
  return isMultiVariantControl ? `---` : `${chanceToWin}%`;
}
export function dataHasControl(
  variants: Array<ExperienceVariant>,
  data: SummaryResponse,
) {
  const dataVariantIds = data?.variants?.map((v) => v.variantId);
  return !!variants
    ?.filter((v) => dataVariantIds.includes(v.id))
    ?.map((v) => v.name)
    ?.find((n) => n === CONTROL);
}

const filterActiveVariants = (
  variants: Array<ExperienceVariant>,
  version: ExpVersion,
  status: ExperienceStatus,
) => {
  let _variants = variants;
  if (version?.isCurrent && status === ExperienceStatus.PUBLISHED) {
    _variants = variants.filter((v) => v.publishedChance > 0);
  }

  const variant100 = _variants.find((v) => v.publishedChance === 100);
  if (variant100 && version?.isCurrent) {
    _variants = [variant100];
  }
  return _variants;
};
